import { SERVER_MAX_PAGE_MM } from './mapPrintLimits'

/**
 * A custom sheet's bounds, in millimetres of TOTAL page — margins included,
 * because this is the number the user sets and the number printed on the
 * button. The map area is 2 x `DEFAULT_PRINT_MARGIN_MM` smaller on each axis,
 * which at the 100 mm minimum means 80 mm of map.
 */
export const MIN_PAGE_MM = 100
export const MAX_PAGE_MM = SERVER_MAX_PAGE_MM

/**
 * Width over height. A4 portrait is ~0.707; landscape ~1.414.
 *
 * Four to one either way, and no further. The range used to run to ten, which
 * offered sheets nobody prints — a map ten times as wide as it is tall is not a
 * hunting ground, it is a bookmark — and spent most of the slider's travel
 * getting there. A quarter to four keeps every shape a wall map is actually cut
 * to, and puts the square in the middle of the travel.
 */
export const MIN_PAGE_ASPECT = 0.25
export const MAX_PAGE_ASPECT = 4

export interface PageMm {
  w: number
  h: number
}

export function pageAspect({ w, h }: PageMm): number {
  return h > 0 ? w / h : 1
}

/** Whole millimetres inside the offered range, on both edges. */
export function clampPage({ w, h }: PageMm): PageMm {
  const clamp = (v: number) => Math.min(MAX_PAGE_MM, Math.max(MIN_PAGE_MM, Math.round(v)))
  return { w: clamp(w), h: clamp(h) }
}

/**
 * The page at a requested width-over-height ratio.
 *
 * Height is held and width derived, which is what the slider promises. Where
 * that lands outside 100..2000 mm the page is scaled as a WHOLE — both edges by
 * the same factor — until it fits, so the ratio the user asked for is always
 * the ratio they get.
 *
 * Scaling both edges is the whole point, and the earlier version that clamped
 * them one at a time is why this needs saying. Independent clamping silently
 * substitutes a different shape: a 40 mm-tall anchor at ratio 2 became a
 * SQUARE 100 x 100 mm page, because the width was raised to the 100 mm minimum
 * and the height was then raised to it as well. That is reachable from the
 * default custom sheet — 1:50 000 over a few kilometres of ground — and it
 * leaves the slider visibly inert, since the ratio it just set is not the ratio
 * that comes back.
 *
 * It never has to refuse a ratio. The page range spans a factor of 20 and the
 * ratio range only a factor of 4 either way, so a legal page exists for every
 * ratio in `MIN_PAGE_ASPECT`..`MAX_PAGE_ASPECT` and the two bounds below can
 * never cross.
 */
export function pageForAspect(page: PageMm, aspect: number): PageMm {
  const wanted = Math.min(MAX_PAGE_ASPECT, Math.max(MIN_PAGE_ASPECT, aspect))
  const h = Math.max(1e-9, page.h)
  const w = h * wanted

  // How far the page may be scaled before an edge leaves the range: the SHORT
  // edge sets the floor and the long edge the ceiling.
  const minScale = MIN_PAGE_MM / Math.min(w, h)
  const maxScale = MAX_PAGE_MM / Math.max(w, h)
  const factor = Math.min(maxScale, Math.max(minScale, 1))

  // `clampPage` still runs, for the whole-millimetre rounding and as the
  // backstop against a degenerate input; by here it has nothing left to clamp.
  return clampPage({ w: w * factor, h: h * factor })
}
